import { useMemo } from "react";
import type { CollabPreview } from "@cabal/shared/domain/session/collab/types.js";
import type { ReflectionPreview } from "@cabal/shared/domain/session/reflection/types.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type { UserShortIdView } from "@cabal/shared/domain/session/user-short-id/types.js";
import type {
  VersionPreview,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import type { AttachedFileView } from "@cabal/shared/domain/upload/types.js";
import type { SessionDataContextValue, SessionCollab } from "./context";
import {
  selectFromShortId,
  selectToShortId,
} from "./short-id-index-selectors";

interface UseSessionShortIdIndexesInput {
  products: Record<string, ProductSummary>;
  versions: Record<string, VersionSummary>;
  attachedFiles: Record<string, AttachedFileView>;
  members: Record<string, Member>;
  collabs: Record<string, SessionCollab>;
  chatMessages: Record<string, SessionCollab>;
  versionPreviews: Record<string, VersionPreview>;
  collabPreviews: Record<string, CollabPreview>;
  reflectionPreviews: Record<string, ReflectionPreview>;
  userShortIdViews: UserShortIdView[];
}

interface SessionShortIdIndexes {
  fromShortId: SessionDataContextValue["fromShortId"];
  toShortId: SessionDataContextValue["toShortId"];
}

export function useSessionShortIdIndexes({
  products,
  versions,
  attachedFiles,
  members,
  collabs,
  chatMessages,
  versionPreviews,
  collabPreviews,
  reflectionPreviews,
  userShortIdViews,
}: UseSessionShortIdIndexesInput): SessionShortIdIndexes {
  const fromShortId = useMemo(() => selectFromShortId({
    products,
    versions,
    attachedFiles,
    members,
    collabs,
    chatMessages,
    versionPreviews,
    collabPreviews,
    reflectionPreviews,
    userShortIdViews,
  }), [
    products,
    versions,
    attachedFiles,
    members,
    collabs,
    chatMessages,
    userShortIdViews,
    versionPreviews,
    collabPreviews,
    reflectionPreviews,
  ]);

  const toShortId = useMemo(() => selectToShortId({
    products,
    versions,
    attachedFiles,
    members,
    collabs,
    chatMessages,
    versionPreviews,
    collabPreviews,
    reflectionPreviews,
    userShortIdViews,
  }), [
    products,
    versions,
    attachedFiles,
    members,
    collabs,
    chatMessages,
    userShortIdViews,
    versionPreviews,
    collabPreviews,
    reflectionPreviews,
  ]);

  return { fromShortId, toShortId };
}
